import { useCallback, useContext } from 'react';
import axios from 'src/axios';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import { toStoredDate } from 'src/util/util';
import { mergeSavedRecords, savedListFrom } from '../../../recordRouteResponse';
import {
  RECORD_KIND,
  alreadyRecorded,
  buildLegendRecordPayload,
  ownerIdOf
} from './legendRecordPayload';
import { DROP_REFUSAL, refusalMessage } from './resolveDropTarget';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Writing a dropped disease, phenotype or genotype to a person — the way the
// sidebar's own Phenotypes / Genotypes / Diseases sections do it.
//
// The drop used to take a shortcut the sidebar never takes: it patched the chart's
// records and stopped. It never moved the pedigree into saveStatus.IN_PROGRESS,
// so it took no edit lock and armed no save. Every sidebar add does both
// (`updateData` in each menu): the lock is how another clinician is kept out
// while this one edits, and the save re-asserts the person's arrays onto the row
// the chart actually draws. Without it, a drop was the one clinical write on this
// screen that bypassed both.
//
// And it patched from the records captured when the drag started
// (`setDataset(dataset.map(...))`), so a second drop — or anything else — landing
// during the round trip was silently reverted. The patch is functional now, and
// it MERGES what was added into the drawn person's own list rather than copying
// the server's list over it (see mergeSavedRecords for why that matters once the
// save is armed).

const ENDPOINT = {
  [RECORD_KIND.disease]: APICONSTANTS.diagnoses,
  [RECORD_KIND.phenotype]: APICONSTANTS.phenotypes,
  [RECORD_KIND.genotype]: APICONSTANTS.genotypes
};

const FIELD = {
  [RECORD_KIND.disease]: 'diagnoses',
  [RECORD_KIND.phenotype]: 'phenotypes',
  [RECORD_KIND.genotype]: 'genotypes'
};

const LEGEND_EVENT = {
  [RECORD_KIND.disease]: EVENTS.SET_PEDIGREE_DIAGNOSES,
  [RECORD_KIND.phenotype]: EVENTS.SET_PEDIGREE_PHENOTYPES,
  [RECORD_KIND.genotype]: EVENTS.SET_PEDIGREE_GENOTYPES
};

const SUCCESS = {
  [RECORD_KIND.disease]: 'Diagnosis assigned successfully',
  [RECORD_KIND.phenotype]: 'Phenotype assigned successfully',
  [RECORD_KIND.genotype]: 'Genotype assigned successfully'
};

/** The same person, by stable identity where both rows have it. */
export const sameMember = (a, b) =>
  a?._id != null && b?._id != null
    ? String(a._id) === String(b._id)
    : a?.pid != null && a.pid === b?.pid;

/**
 * @param {object} opts
 * @param {Function} opts.setDataset  setMemberRecords
 * @param {Function} [opts.setPedigreeState]  arms the save; absent = no save armed
 * @param {(target: object) => string|null} [opts.dropBlocker]  a reason this
 *   person cannot be edited right now (locked by another user, bulk edit), or null
 * @param {boolean} [opts.enabled=true]  the legend may write at all
 * @returns {{
 *   checkTarget: (item: object, target: object, kind: string) => object|null,
 *   write: (args: {member, item, kind, age?, comment?}) => Promise<boolean>
 * }}
 */
export const useLegendRecordWriter = ({
  setDataset,
  setPedigreeState,
  dropBlocker,
  enabled = true
}) => {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const { content, dispatchUpdate } = useContext(AppContext);
  const selectedUser = content?.pedigreeSelectedUsers?.selectedUser;
  const recordedBy = useLoggedInUserName();

  /**
   * The person a drop may write to, or null having told the user why not. Run
   * BEFORE anything else happens — before the disease age dialog opens, so nobody
   * types an age for a drop that was never going to save — and again when that
   * dialog is saved, because the lock can change hands while it is open.
   */
  const checkTarget = useCallback(
    (item, target, kind) => {
      const refuse = (msg, severity = 'warning') => {
        setSnack({ status: true, msg, severity });
        return null;
      };

      if (!enabled) {
        return refuse(t('This pedigree cannot be edited right now.'));
      }
      if (!target?.member) {
        return refuse(
          refusalMessage(target?.reason, item?.name),
          target?.reason === DROP_REFUSAL.outOfRange ? 'info' : 'warning'
        );
      }
      const blocked = dropBlocker?.(target);
      if (blocked) return refuse(blocked);
      if (!ownerIdOf(selectedUser) || !selectedUser?.familyId) {
        return refuse(t('Please save the pedigree first'), 'error');
      }
      if (alreadyRecorded(kind, target.member, item)) {
        return refuse(
          t('{{name}} is already recorded for this person.', {
            name: item.name
          }),
          'info'
        );
      }
      return target.member;
    },
    [enabled, dropBlocker, selectedUser, setSnack]
  );

  const write = useCallback(
    async ({ member, item, kind, age, comment }) => {
      const field = FIELD[kind];
      const payload = buildLegendRecordPayload({
        kind,
        selectedUser,
        member,
        item,
        age,
        comment,
        recordedBy,
        recordedOn: toStoredDate(new Date())
      });

      try {
        const res = await axios.post(ENDPOINT[kind], payload);
        const saved = savedListFrom(field, res);
        if (!res?.data?.success || !saved) {
          setSnack({
            status: true,
            msg: res?.data?.message || t('The record could not be saved.'),
            severity: 'error'
          });
          return false;
        }

        const added = saved.filter((r) => r?.id === item.id);
        setDataset((prev) =>
          (prev || []).map((row) =>
            sameMember(row, member)
              ? {
                  ...row,
                  [field]: mergeSavedRecords(row[field], saved, [item.id])
                }
              : row
          )
        );
        setPedigreeState?.((prev) => ({
          ...prev,
          save: PEDIGREE.saveStatus.IN_PROGRESS
        }));
        // The family legend gets what was added — never the rest of the list
        // the route returned, which is not necessarily this person's.
        dispatchUpdate({ type: LEGEND_EVENT[kind], value: added });
        setSnack({ status: true, msg: t(SUCCESS[kind]), severity: 'success' });
        return true;
      } catch (err) {
        // `error` from handleGeneralError; `message` from secondaryRecordGuard's
        // 403 ("This is a secondary record…"), which used to be replaced with a
        // generic "An error has occurred!".
        setSnack({
          status: true,
          msg:
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            t('An error has occurred!'),
          severity: 'error'
        });
        return false;
      }
    },
    [
      selectedUser,
      recordedBy,
      setDataset,
      setPedigreeState,
      dispatchUpdate,
      setSnack
    ]
  );

  return { checkTarget, write };
};
